import * as log from 'loglevel';
import Request from '../request';
import Translate from '../translate';

import {html} from 'lit-html';
import {repeat} from 'lit-html/directives/repeat';
import {classMap} from 'lit-html/directives/class-map';
import {styleMap} from 'lit-html/directives/style-map';
import {ifDefined} from 'lit-html/directives/if-defined';
import {live} from 'lit-html/directives/live';

import TemplateRegistry from '../template/template-registry';
import {cdnImg, optionName} from '../template/template-util.js';
import {AppExtensions} from '../const';

const STYLE_NONE = 'none';

import './ai.option.css';

class AiController {

    constructor(onChange, getOption) {
        this.onChange = onChange;
        this.getOption = getOption;
        this.config = null; // reference updated on each render
        this.option = null; // reference updated on each render

        // global settings
        this.settings = AiController.settings;

        // style via assets firebase to to cache  SAME LOGIC: app-options.js
        this.settings.cdn = this.settings.api.indexOf('local') === -1 ? this.settings.api.replace('api.', 'assets.') : this.settings.api;
    }

    init() {
        // prompt input
        this.prompt = '';

        // system prompt (from params, not visible to user)
        this.systemPrompt = '';

        // currently selected image
        this.selectedImage = null;
        
        // loading state
        this.isLoading = false;
        
        // error messages
        this.errors = {};
        
        // failure counter for retry logic
        this.failures = 0;
        this.failuresEdit = 0;

        // generation history (keep images from previous generations)
        // Load from localStorage if available (config/option are set before init() is called)
        this.imageHistory = this.loadHistory();
        
        // selected style
        this.selectedStyle = STYLE_NONE;
        
        // editing mode state
        this.editingMode = false;
        this.selectedImageForEdit = null;
        this.editPrompt = '';
        
        // pagination state
        this.displayedImagesCount = 0;

        // original upload image reference for re-styling
        this.originalUploadImage = null;
    }

    initUI() {

        // Tokens select the catalogue model; the server derives its tier for fallbacks.
        this.token = window.Cloudlift.options.config?.ai?.token;
        this.tokenEdit = window.Cloudlift.options.config?.ai?.tokenEdit;
        this.tokenBg = window.Cloudlift.options.config?.ai?.tokenBg;
        // Send the preference even if the selected model cannot honour it: a fallback may support alpha.
        this.transparent = window.Cloudlift.options.config?.ai?.transparent === true || undefined;
        this.transparentEdit = window.Cloudlift.options.config?.ai?.transparentEdit === true || undefined;

        // Set system prompt if provided)
        if (this.option.params.aiPrompt) {
            // Store as internal system prompt that gets automatically applied
            this.systemPrompt = this.option.params.aiPrompt;
        }

        // ai features toggles
        this.aiGenerate = this.option.params.aiEditor?.generate || false;
        this.aiStyle   = this.option.params.aiEditor?.style || false;
        this.aiEdit     = this.option.params.aiEditor?.edit || false;
        this.aiRemoveBg = this.option.params.aiEditor?.removeBg || false;

        this.aiRemoveBgAuto = this.option.params.aiRemoveBgAuto || false;
        // DEPRECATED: auto enabled
        // this.aiStyleAuto = this.option.params.aiStyleAuto || false;

        this.aiRatio = this.option.params.aiRatio || undefined;

        // Pagination configuration
        this.aiPageSize = this.option.params.aiPageSize || 6;
        this.aiImagesMax = this.option.params.aiImagesMax ?? (this.aiPageSize * 3); // max history size (default 3 pages)

        // Initialize displayed images count
        if (this.displayedImagesCount === 0) {
            this.displayedImagesCount = Math.min(this.aiPageSize, this.imageHistory.length);
        }

        // all available styles
        this.styles = window.Cloudlift.options.config?.ai?.styles || [];

        // select specific styles (by id)
        if (Array.isArray(this.option.params.aiStyles) && this.option.params.aiStyles.length > 0) {
            this.styles = this.styles.filter(style => this.option.params.aiStyles.includes(style.id));
        } else if(this.aiGenerate){
            // special case - remove edit styles if image generation is enabled but no styles are selected (only works for system styles)
            this.styles = this.styles.filter(style => !style.id.startsWith('edit_'));
        }

        // move 'none' style to front if included
        const noneIndex = this.styles.findIndex(style => style.id === STYLE_NONE);
        if (noneIndex > 0) {
            const [noneStyle] = this.styles.splice(noneIndex, 1);
            this.styles.unshift(noneStyle);
        }

        // set default style (editor mode calls this again on each render)
        if (!this.selectedStyle || this.selectedStyle === STYLE_NONE) {
            this.selectedStyle = this.styles.length ? this.styles[0].id : STYLE_NONE;
        }

        // upload option link
        if(this.option.params.optionUpload) {

            // sync upload option value with this option
            this.uploadSync = this.option.params.uploadSync || false;

            // editorMode: de-register previous event handler
            if (this.optionChanged) {
                this.config.productForm.removeEventListener('option:changed', this.optionChanged);
            }

            this.optionChanged = this.onOptionChanged.bind(this);

            // listen to all option changes
            this.config.productForm.addEventListener('option:changed', this.optionChanged);
        }

        // Live preview edit support
        if (this.option.params.aiLivePreview) {
            window.addEventListener('options:rendered', (e) => {
                this._productOptions = e.detail; // ProductOptions instance
            }, {once: true});
        }

        // Initialize drag scrolling for style selector (only once)
        if (this.styles.length > 4 && !this._dragScrollInitialized) {
            this._dragScrollInitialized = true;
            window.addEventListener('options:rendered', () => {
                this.initDragScroll();
            }, {once: true});
        }
    }

    restore(){
        const imgRestore = {
            id: "ai_cart",
            img: this._corsUrl(this.option.value), // IMPORTANT: cors to render on preview
            style: "none"
        }
        // add to history to display
        this.imageHistory.unshift(imgRestore);
        this.displayedImagesCount++;
        // build option values
        this.onImageSelect(null, imgRestore)
    }

    combinePrompts(userPrompt) {
        if (!this.systemPrompt) {
            // No system prompt, return user prompt as is
            return userPrompt || '';
        }

        // Process all placeholders in the system prompt
        let result = this.systemPrompt;

        // Find all placeholders in the format {placeholder}
        const placeholderRegex = /\{([^}]+)\}/g;
        const placeholders = [...result.matchAll(placeholderRegex)];

        // Replace each placeholder with its value
        for (const match of placeholders) {
            const placeholder = match[0]; // e.g., "{prompt}" or "{color}"
            const optionName = match[1]; // e.g., "prompt" or "color"

            let replacementValue = '';

            if (optionName === 'prompt') {
                // Special case for {prompt} - use the userPrompt parameter
                replacementValue = userPrompt || '';
            } else {
                // For other placeholders, use getOption to resolve the value
                const option = this.getOption(optionName);
                replacementValue = option?.value || '';
            }

            // Replace all occurrences of this placeholder
            result = result.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacementValue);
        }

        return result.trim();
    }

    getRatio() {
        if (this.aiRatio) {
            // special case to allow dynamic ratio from another option
            if (this.aiRatio.startsWith('{') && this.aiRatio.endsWith('}')) {
                const optionName = this.aiRatio.slice(1, -1);
                const option = this.getOption(optionName);
                if (option?.value) {
                    return option.value;
                }
            }
            return this.aiRatio;
        }
        return undefined;
    }

    async onGenerateImage() {
        const currentPrompt = this.editingMode ? this.editPrompt : this.prompt;

        if (!currentPrompt.trim()) {
            this.errors.general = Translate.get('product.options.ai.error.empty_prompt', null, 'Please enter a prompt');
            this.renderOptions();
            return;
        }

        // Clear previous errors and set loading state
        this.errors = {};
        this.isLoading = true;
        this.renderOptions();

        // Combine system prompt with current prompt
        const combinedPrompt = this.combinePrompts(currentPrompt);
        let result;
        if (this.editingMode && this.selectedImageForEdit) {
            result = await this.editImage(this.selectedImageForEdit, combinedPrompt);
        } else {
            result = await this.generateImage(combinedPrompt);
        }
        await this.handleResult(result);
    }

    async handleResult(result) {
        this.isLoading = false;
        
        if (result) {
            // Success - image was generated or edited
            this.imageHistory.unshift(result);

            // apply max history size
            if (this.imageHistory.length > this.aiImagesMax) {
                this.imageHistory = this.imageHistory.slice(0, this.aiImagesMax);
            }
            // save to localStorage after updating history
            this.saveHistory();

            // Update displayed count to include new image
            this.displayedImagesCount = Math.min(this.aiPageSize, this.imageHistory.length);
            
            // Auto-select the newly generated/edited image
            this.onImageSelect(null, result);
            
            // Auto remove background if enabled and this is a newly generated image
            if (this.aiRemoveBgAuto && !result.backgroundRemoved) {
                // Automatically remove background from newly generated images
                await this.removeBackground(result);
                return; // removeBackground will call handleResult again with the processed image
            }
            
            // Clear editing mode after successful edit
            if (this.editingMode) {
                this.exitEditingMode();
            }
        }
        
        // Update UI
        this.renderOptions();
    }

    onPromptChange(e) {
        if (this.editingMode) {
            this.editPrompt = e.target.value;
        } else {
            this.prompt = e.target.value;
        }
        
        // Clear errors when user starts typing
        if (Object.keys(this.errors).length > 0) {
            this.errors = {};
            this.renderOptions();
        }
    }

    onStyleClick(styleId) {
        // Skip if already selected
        if (this.selectedStyle === styleId) return;

        this.selectedStyle = styleId;

        // For simple style changes without image changes, just update UI
        if (!this.originalUploadImage && !this.isLoading) {
            const styleItems = document.querySelectorAll('.cl-po--ai-style-item');
            const selectedIndex = this.styles.findIndex(style => style.id === this.selectedStyle);

            styleItems.forEach((item, index) => {
                if (index === selectedIndex) {
                    item.classList.add('cl-po--ai-style-selected');
                } else {
                    item.classList.remove('cl-po--ai-style-selected');
                }
            });
            return;
        }

        // Smart style switching for uploaded images
        if (this.originalUploadImage) { // this.aiStyleAuto &&
            // Look for existing image with this style from same original
            const existingStyled = this.imageHistory.find(img => 
                img.originalImage?.id === this.originalUploadImage.id && 
                (img.style === this.selectedStyle || 
                 (this.selectedStyle === STYLE_NONE && img.id === this.originalUploadImage.id))
            );
            
            if (existingStyled) {
                // Auto-select existing styled version
                this.onImageSelect(null, existingStyled);
                this.renderOptions();
                return;
            }
            
            // Generate new style if doesn't exist
            if (this.selectedStyle !== STYLE_NONE) {
                // re-apply style to original upload
                this.uploadImage = {...this.originalUploadImage};
                this.editUpload();
                return;
            } else {
                // Select original unstyled version
                this.onImageSelect(null, this.originalUploadImage);
                this.renderOptions();
                return;
            }
        }

        this.renderOptions();
    }

    onImageSelect(e, image) {
        // Remove previous image from option values if exists
        if (this.selectedImage) {
            const index = this.option.values?.indexOf(this.selectedImage);
            if (index > -1) {
                this.option.values.splice(index, 1);
            }
        }

        this.selectedImage = image;
        
        // Add to values for validation & price calculation support
        if (!this.option.values) {
            this.option.values = [];
        }
        this.option.values.push(image);
        
        // Save selected image URL as value
        this.option.value = image.img;

        // sync current image to upload option if enabled (useful to keep the live preview connected to the upload option)
        if(this.uploadSync){
            this.getOption(this.option.params.optionUpload).value = this.option.value;
        }
        
        // Trigger change
        this.onChange(this.option);
    }


    isSelected(option, optionValue) {
        return this.selectedImage === optionValue;
    }

    onValidate(option) {
        // Custom validation
        if (option.required && !this.selectedImage) {
            option.errors.push(Translate.get('product.options.validity.valueMissing'));
            return false;
        }
        return true;
    }

    renderOptions() {
        this.config.productForm.dispatchEvent(new CustomEvent('options:render', { 
            detail: this.option, 
            cancelable: true, 
            bubbles: true 
        }));
        
    }

    aiStyleMap(styleMap = {}) {
        // Add color customization
        if(this.option.params.aiColor) {
            styleMap['--ai-primary-color'] = this.option.params.aiColor;
        }
        if(this.option.params.aiImgSize) {
            styleMap['--ai-image-size'] = this.option.params.aiImgSize + 'px';
        }
        return styleMap;
    }

    handleApiError(error, operation = 'generate') {
        const status = error.xhr?.status;
        const errorMessage = error.data?.error || error.statusText || 'Operation failed';

        // Categorize errors by HTTP status
        switch(status) {
            case 429: // Rate limit
                this.errors.general = Translate.get('product.options.ai.error.rate_limit', null, 'Too many requests. Please wait a moment and try again.');
                // DON'T increment failure counters for rate limits
                return { shouldRetry: false, isRateLimit: true };

            case 402: // Payment required
                this.errors.general = Translate.get('product.options.ai.error.payment_required', null, 'Not enough credits.');
                return { shouldRetry: false };

            case 400: // Bad request
                this.errors.general = errorMessage || Translate.get('product.options.ai.error.bad_request', null, 'Invalid request. Please check your input.');
                return { shouldRetry: false };

            case 408: // Timeout
                this.errors.general = Translate.get('product.options.ai.error.timeout', null, 'Request timed out. Please try again.');
                if (operation === 'edit') {
                    this.failuresEdit++;
                } else if (operation === 'generate') {
                    this.failures++;
                }
                return {shouldRetry: true};

            // case 500: all other server errors
            default:
                this.errors.general = Translate.get('product.options.ai.error.unavailable', null, 'Service temporarily unavailable. Please try again.');
                // Increment appropriate failure counter for server errors
                if (operation === 'edit') {
                    this.failuresEdit++;
                } else if (operation === 'generate') {
                    this.failures++;
                }
                return {shouldRetry: true};
        }
    }

    async generateImage(prompt) {
        try {

            const requestBody = {
                prompt: prompt,
                token: this.token,
                transparent: this.transparent,
                // each retry walks to the next fallback of the shop's model (max 2 fallbacks)
                retry: this.failures > 0 ? Math.min(this.failures, 2) : undefined,
                // the api applies the ratio only when the served model supports it
                ratio: this.getRatio(),
                /* DEV
                sleep: 1,
                url: 'https://replicate.delivery/xezq/XXlwcQkqG9YKOBZFkXjOklIBepzjfMNcu4cc2Ax6Fkw900RVA/out-0.webp' */
            };

            // Add style if not 'none'
            if (this.selectedStyle !== STYLE_NONE) {
                requestBody.style = this.selectedStyle;
            }

            const response = await new Request(this.settings.api + '/api/ai/image/generate', {
                method: 'POST',
                responseType: 'json',
                headers: {
                    'Content-type': 'application/json'
                },
                data: requestBody,
                queryParams: this.settings.queryParams
            });

            if (response.data.error) {
                this.errors.general = response.data.error;
                this.failures++;
                return null;
            } else if (response.data.url) {
                this.failures = 0; // back to the shop's model after a fallback succeeded
                return {
                    id: `ai_${Date.now()}`,
                    img: response.data.url,
                    prompt: prompt,
                    style: this.selectedStyle,
                    // the shop asked for an alpha background and the served model delivered one, so there is no
                    // background left to remove: skips the automatic call below and disables the manual button.
                    // absent when a retry fell to a model that cannot, which is why this is read per image
                    backgroundRemoved: !!response.data.transparent
                };
            }
        } catch (error) {
            log.error('AI image generation request failed:', error);
            this.handleApiError(error, 'generate');
            return null;
        }
    }

    async editImage(image, prompt) {
        try {
            const requestBody = {
                prompt: prompt,
                url: image.img,
                data: image.data, // optional base64 image data to directly upload
                // each retry walks to the next fallback of the shop's model (max 2 fallbacks)
                retry: this.failuresEdit > 0 ? Math.min(this.failuresEdit, 2) : undefined,
                token: this.tokenEdit,
                transparent: this.transparentEdit,
                ratio: this.getRatio() // the api applies the ratio only when the served model supports it
            };

            // Only add style to request if image doesn't already have this style
            if (this.selectedStyle !== STYLE_NONE && image.style !== this.selectedStyle) {
                requestBody.style = this.selectedStyle;
            }

            const response = await new Request(this.settings.api + '/api/ai/image/edit', {
                method: 'POST',
                responseType: 'json',
                headers: {
                    'Content-type': 'application/json'
                },
                data: requestBody,
                queryParams: this.settings.queryParams
            });

            if (response.data.error) {
                this.errors.general = response.data.error;
                this.failuresEdit++;
                return null;
            } else if (response.data.url) {
                this.failuresEdit = 0; // back to the shop's model after a fallback succeeded
                return {
                    id: `ai_edit_${Date.now()}`,
                    img: response.data.url,
                    prompt: prompt,
                    style: this.selectedStyle,
                    originalImage: image,
                    type: 'edit',
                    // as in generateImage: an edit that came back with alpha needs no removal
                    backgroundRemoved: !!response.data.transparent
                };
            }
        } catch (error) {
            log.error('AI image editing request failed:', error);
            this.handleApiError(error, 'edit');
            return null;
        }
    }

    async removeBackground(image) {
        this.isLoading = true;
        this.renderOptions();

        try {
            const requestBody = {
                url: image.img,
                token: this.tokenBg
            };

            const response = await new Request(this.settings.api + '/api/ai/image/removebg', {
                method: 'POST',
                responseType: 'json',
                headers: {
                    'Content-type': 'application/json'
                },
                data: requestBody,
                queryParams: this.settings.queryParams
            });

            if (response.data.error) {
                this.errors.general = response.data.error;
                await this.handleResult(null);
            } else if (response.data.url) {
                const result = {
                    id: `ai_removebg_${Date.now()}`,
                    img: response.data.url,
                    prompt: 'Background removed',
                    style: 'background_removed',
                    originalImage: image,
                    type: 'removebg',
                    backgroundRemoved: true
                };
                await this.handleResult(result);
            }
        } catch (error) {
            log.error('AI background removal request failed:', error);
            this.handleApiError(error, 'removebg');
            await this.handleResult(null);
        }
    }

    onEditImage(image) {
        // Use the provided image or fall back to selected image
        const imageToEdit = image || this.selectedImage;
        if (!imageToEdit) {
            this.errors.general = Translate.get('product.options.ai.error.no_image_selected', null, 'Please select an image first');
            this.renderOptions();
            return;
        }
        
        this.editingMode = true;
        this.selectedImageForEdit = imageToEdit;
        this.editPrompt = '';
        this.renderOptions();
    }

    exitEditingMode() {
        this.editingMode = false;
        this.selectedImageForEdit = null;
        this.editPrompt = '';
    }

    onCancelEdit() {
        this.exitEditingMode();
        this.renderOptions();
    }

    async onOptionChanged (e){
        let option = e.detail;
        // check if referenced option changed
        if (option.name === this.option.params.optionUpload) {
            if (option.value) {
                // store reference to uploaded image for simpler remove
                this.uploadImage = {
                    id: `${option.name}_${Date.now()}`,
                    img: option.value,
                };

                // Store original for re-styling
                this.originalUploadImage = {...this.uploadImage};

                // Apply system prompt and/or style if either exists
                if (this.systemPrompt || (this.selectedStyle !== STYLE_NONE)) { // this.aiStyleAuto &&
                    await this.editUpload();
                } else {
                    // No system prompt or style - use raw image
                    await this.handleResult(this.uploadImage);
                }

                // auto edit mode if enabled
                if (this.aiEdit) {
                    this.onEditImage(this.selectedImage);
                }
            } else if (this.uploadImage) {
                // remove from history and selection
                this.imageHistory = this.imageHistory.filter(img => img.originalImage?.id !== this.uploadImage.id);

                // Save to localStorage after removing images
                this.saveHistory();

                if (this.selectedImageForEdit?.originalImage?.id === this.uploadImage.id) {
                    this.exitEditingMode();
                }
                if (this.selectedImage?.originalImage?.id === this.uploadImage.id) {
                    this.selectedImage = null;
                    this.option.value = null;
                    this.onChange(this.option);
                }
                delete this.uploadImage;
            }

        } else if (this.uploadImage && this.option.name !== option.name) {

            // any other option changed > mark upload as dirty to allow re-apply prompt/style
            if(this.option.params.aiLivePreview){ // only required when live preview is included in input
                this.uploadImage.dirty = true;
            }
        }
    }

    async editUpload(){
        // Set loading state and update UI immediately
        this.isLoading = true;
        this.renderOptions();

        if (this.option.params.aiLivePreview && this._productOptions) {
            let previews = this._productOptions._getPreviews();
            if(previews?.length){
                let preview = previews[0]; // default to first
                // view by name
                if(previews.length > 1 && this.option.params.aiLivePreviewView){
                    for(const p of previews){
                        if(p.previewConfig.name === this.option.params.aiLivePreviewView){
                            preview = p;
                            break;
                        }
                    }
                }
                // store data to upload image for editing
                this.uploadImage.data = preview.export({
                    width: 1024,
                    height: 1024
                }, false, false,
                    (obj) => {
                        // exclude layers connected to this AI option to avoid recursion
                        if(obj._layer?.params?.option === this.option.name){
                            return true;
                        }
                        // also exclude the upload layer if the AI option is linked to an upload option
                        else if(obj._layer?.params?.option === this.option.params.optionUpload){
                            return true;
                        }

                        return false;
                    },
                    'jpeg'); // jpeg for smaller size
            }
        }

        // Apply system prompt and/or style to the upload
        const uploadPrompt = this.combinePrompts('the image');
        const styledResult = await this.editImage(this.uploadImage, uploadPrompt);
        if (styledResult) {
            await this.handleResult(styledResult);
        } else {
            // Fallback to raw image if processing fails
            await this.handleResult(this.uploadImage);
        }
    }

    onLoadMore() {
        const remainingImages = this.imageHistory.length - this.displayedImagesCount;
        const imagesToAdd = Math.min(this.aiPageSize, remainingImages);
        this.displayedImagesCount += imagesToAdd;
        this.renderOptions();
    }

    getStorageKey() {
        // Generate unique key per option
        return `cl_ai_${this.option?.name}`;
    }

    saveHistory() {
        try {
            const storageKey = this.getStorageKey();

            // Store only minimal data: id, img, style, and whether the image already has no background.
            // That last one has to survive: without it a restored cut-out looks like an ordinary image, the
            // button offers to remove a background that is already gone, and the customer pays a credit for it
            const minimalImages = this.imageHistory.slice(0, this.aiImagesMax).map(img => ({
                id: img.id,
                img: img.img,
                style: img.style,
                ...(img.backgroundRemoved ? {backgroundRemoved: true} : {})
            }));

            const storageData = {
                timestamp: Date.now(),
                images: minimalImages
            };

            localStorage.setItem(storageKey, JSON.stringify(storageData));
        } catch (error) {
        }
    }

    loadHistory() {
        try {

            // clear history on add to cart (avoid users seeing old images when returning to product)
            window.addEventListener('options:submit', () => {
                this.clearHistory();
            }, {once: true});

            const storageKey = this.getStorageKey();
            const stored = localStorage.getItem(storageKey);

            if (!stored) {
                return [];
            }

            const data = JSON.parse(stored);

            // Validate timestamp - only restore if less than 1 day old
            const ONE_DAY = 24 * 60 * 60 * 1000;
            const age = Date.now() - (data.timestamp || 0);

            if (age > ONE_DAY) {
                // Expired - delete and return empty
                this.clearHistory();
                return [];
            }

            // Validate data structure
            if (!Array.isArray(data.images)) {
                this.clearHistory();
                return [];
            }

            return data.images;
        } catch (error) {
            // Corrupted data - clear and return empty
            this.clearHistory();
            return [];
        }
    }

    clearHistory() {
        try {
            const storageKey = this.getStorageKey();
            localStorage.removeItem(storageKey);
        } catch (error) {
        }
    }

    _corsUrl(url) {
        // CORS fix  TODO better cors url - or maybe CORS url is generated in the upload layer
        url = url.replace('https://upload.dev.cloudlift.app/s/dev', 'https://storage.googleapis.com/cloudlift-app-cloud-dev-upload');
        url = url.replace('https://upload.cloudlift.app/s', 'https://storage.googleapis.com/cloudlift-app-cloud-prod-upload');
        return url;
    }

    initDragScroll() {
        // Skip on touch devices - they have native touch scrolling
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;

        const container = document.querySelector('.cl-po--ai-style-horizontal');
        if (!container) return;

        // Check if already initialized for this container
        if (container.dataset.dragScrollInit === 'true') return;
        container.dataset.dragScrollInit = 'true';

        let isDown = false;
        let startX;
        let scrollLeft;
        let hasDragged = false;
        const dragThreshold = 5; // pixels of movement before considering it a drag

        const handleMouseDown = (e) => {
            // Prevent default to stop image dragging
            e.preventDefault();

            isDown = true;
            hasDragged = false;
            container.style.cursor = 'grabbing';
            container.style.userSelect = 'none'; // Prevent text selection during drag
            startX = e.pageX;
            scrollLeft = container.scrollLeft;
        };

        const handleMouseMove = (e) => {
            if (!isDown) return;

            e.preventDefault(); // Always prevent default when dragging
            const x = e.pageX;
            const distance = Math.abs(startX - x);

            // Mark as dragged if we've moved beyond threshold
            if (distance > dragThreshold) {
                hasDragged = true;
            }

            // Always scroll when mouse is down and moving
            const walk = (startX - x) * 2;
            container.scrollLeft = scrollLeft + walk;
        };

        const handleMouseUp = (e) => {
            if (!isDown) return;

            isDown = false;
            container.style.cursor = 'grab';
            container.style.userSelect = ''; // Re-enable text selection

            // If we dragged, prevent the click event on style items
            if (hasDragged) {
                const styleItem = e.target.closest('.cl-po--ai-style-item');
                if (styleItem) {
                    // Prevent the click event that follows mouseup
                    const preventClick = (evt) => {
                        evt.preventDefault();
                        evt.stopPropagation();
                        // Remove listener after preventing once
                        setTimeout(() => {
                            container.removeEventListener('click', preventClick, true);
                        }, 0);
                    };
                    container.addEventListener('click', preventClick, true);
                }
            }

            hasDragged = false;
        };

        const handleMouseLeave = () => {
            if (isDown) {
                isDown = false;
                hasDragged = false;
                container.style.cursor = 'grab';
                container.style.userSelect = '';
            }
        };

        container.addEventListener('mousedown', handleMouseDown);
        container.addEventListener('mousemove', handleMouseMove);
        container.addEventListener('mouseup', handleMouseUp);
        container.addEventListener('mouseleave', handleMouseLeave);

        // Also stop on window blur to handle edge cases
        window.addEventListener('blur', handleMouseLeave);

        // Add grab cursor style
        container.style.cursor = 'grab';
    }
    
    scrollToPrevStyle() {
        const container = document.querySelector('.cl-po--ai-style-horizontal');
        const styleItems = container?.querySelectorAll('.cl-po--ai-style-item');
        if (!container || !styleItems?.length) return;

        const containerRect = container.getBoundingClientRect();
        const scrollLeft = container.scrollLeft;

        // Find the first item that's partially or completely out of view on the left
        let targetItem = null;
        let targetScrollPosition = 0;

        for (let i = styleItems.length - 1; i >= 0; i--) {
            const item = styleItems[i];
            const itemRect = item.getBoundingClientRect();
            const itemRelativeLeft = itemRect.left - containerRect.left;

            // If item is to the left of the container viewport
            if (itemRelativeLeft < -10) {
                targetItem = item;
                // Calculate the scroll position to show this item
                targetScrollPosition = item.offsetLeft;
                break;
            }
        }

        // If no item found to the left, scroll to beginning
        if (!targetItem && scrollLeft > 0) {
            targetScrollPosition = 0;
        } else if (targetItem) {
            // Scroll the container to show the target item
            container.scrollTo({
                left: targetScrollPosition,
                behavior: 'smooth'
            });
            return;
        }

        if (targetScrollPosition !== undefined) {
            container.scrollTo({
                left: targetScrollPosition,
                behavior: 'smooth'
            });
        }
    }
    
    scrollToNextStyle() {
        const container = document.querySelector('.cl-po--ai-style-horizontal');
        const styleItems = container?.querySelectorAll('.cl-po--ai-style-item');
        if (!container || !styleItems?.length) return;

        const containerRect = container.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const scrollLeft = container.scrollLeft;
        const maxScroll = container.scrollWidth - containerWidth;

        // Find the first item that's partially or completely out of view on the right
        let targetItem = null;
        let targetScrollPosition = scrollLeft;

        for (const item of styleItems) {
            const itemRect = item.getBoundingClientRect();
            const itemRelativeRight = itemRect.right - containerRect.right;

            // If item extends beyond the container viewport on the right
            if (itemRelativeRight > 10) {
                targetItem = item;
                // Calculate the scroll position to show this item
                targetScrollPosition = item.offsetLeft;
                break;
            }
        }

        // If no item found to the right, scroll to the end
        if (!targetItem && scrollLeft < maxScroll) {
            targetScrollPosition = maxScroll;
        } else if (targetItem) {
            // Scroll the container to show the target item
            container.scrollTo({
                left: targetScrollPosition,
                behavior: 'smooth'
            });
            return;
        }

        if (targetScrollPosition !== scrollLeft) {
            container.scrollTo({
                left: targetScrollPosition,
                behavior: 'smooth'
            });
        }
    }

    openOverlay(image) {

        // Create overlay elements
        const overlay = document.createElement('div');
        overlay.className = 'cl-po--ai-overlay';
        overlay.setAttribute('data-ai-overlay', 'true');

        const content = document.createElement('div');
        content.className = 'cl-po--ai-overlay-content';

        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'cl-po--ai-overlay-close';
        closeButton.innerHTML = '×';

        const img = document.createElement('img');
        img.src = image.img;
        img.alt = 'Preview';

        // Assemble DOM structure
        content.appendChild(closeButton);
        content.appendChild(img);
        overlay.appendChild(content);

        // Event listeners
        const closeOverlay = () => this.closeOverlay();

        overlay.addEventListener('click', closeOverlay);
        closeButton.addEventListener('click', closeOverlay);
        content.addEventListener('click', (e) => e.stopPropagation());

        // Store reference for cleanup
        this._overlayElement = overlay;

        // Append to body
        document.body.appendChild(overlay);

        // Prevent body scroll when overlay is open
        document.body.style.overflow = 'hidden';
    }

    closeOverlay() {
        // Remove overlay from DOM
        if (this._overlayElement && this._overlayElement.parentNode) {
            this._overlayElement.parentNode.removeChild(this._overlayElement);
        }
        this._overlayElement = null;

        // Restore body scroll
        document.body.style.overflow = '';
    }

}

// Global settings (set in product-options.js)
AiController.settings = {
    assets: '',
    api: '',
    queryParams: {},
};

export default AiController;

// Template registration
TemplateRegistry.templates['ai'] = (option, config, onChange, onErrorClear, getOption) => {

    // extension not enabled
    if (!AppExtensions.AI) {
        return html``;
    }
    
    let init = option._controller === undefined;
    
    // Get or create AI controller
    option._controller = option._controller || new AiController(onChange, getOption);
    
    // Update references
    option._controller.option = option;
    option._controller.config = config;
    
    if (init) {
        option._controller.init();
    }
    
    if (init || config.editorMode) {
        option._controller.initUI();
    }
    // back from cart
    if(option.value && option._controller.selectedImage === null){
        option._controller.restore();
    }
    
    const controller = option._controller;
    
    return html`
        <div class="cl-po--ai" style=${styleMap(controller.aiStyleMap())}>
            
            ${(controller.aiGenerate || controller.aiStyle || controller.editingMode || controller.aiStyleAuto) ? html`
                <div class="cl-po--ai-input-section">
                    
                ${controller.aiStyle && controller.styles.length > 1 ? html`
                    <div class="cl-po--ai-style-selector">
                        <div class="cl-po--ai-style-wrapper">
                            <div class="cl-po--ai-style-horizontal">
                                ${controller.styles.map(style => html`
                                    <div
                                        class="cl-po--ai-style-item ${classMap({ 'cl-po--ai-style-selected': controller.selectedStyle === style.id })}"
                                        @click=${() => controller.onStyleClick(style.id)}
                                    >
                                        <div class="cl-po--ai-style-preview">
                                            ${style.img ? html`<img src="${option._controller.settings.cdn + '/api/ai/style/' + style.img}" alt="${style.name}" />` : null}
                                        </div>
                                        <div class="cl-po--ai-style-name">${style.name}</div>
                                    </div>
                                `)}
                            </div>
                        </div>
                        ${controller.styles.length > 4 ? html`
                            <button
                                class="cl-po--ai-arrow cl-po--ai-arrow-left"
                                @click=${() => controller.scrollToPrevStyle()}
                                tabindex="-1"
                                type="button"
                            >
                                ‹
                            </button>
                            <button
                                class="cl-po--ai-arrow cl-po--ai-arrow-right"
                                @click=${() => controller.scrollToNextStyle()}
                                tabindex="-1"
                                type="button"
                            >
                                ›
                            </button>
                        ` : null}
                    </div>
                ` : null}
                
                ${(controller.aiGenerate || controller.editingMode) ? html`
                <div class="cl-po--ai-prompt">
                    <div class="cl-po--ai-prompt-input ${classMap({ 'cl-po--ai-editing': controller.editingMode })}">
                        <input 
                            type="text" 
                            class="cl-po--input ${classMap({ 'cl-po--editing': controller.editingMode })}" 
                            id="${option.id}_prompt" autocomplete="off"
                            placeholder=${controller.editingMode 
                                ? Translate.get('product.options.ai.edit.placeholder', null, 'Describe how to edit the image...')
                                : Translate.get('product.options.ai.prompt.placeholder', null, 'Describe the image you want to generate...')}
                            .value=${live(controller.editingMode ? controller.editPrompt : controller.prompt)}
                            @input=${(e) => controller.onPromptChange(e)}
                            @keypress=${(e) => e.key === 'Enter' && !controller.isLoading && controller.onGenerateImage()}
                            ?disabled=${controller.isLoading}
                        >
                        <button 
                            class="cl-po--ai-generate-button ${classMap({ 'cl-po--loading': controller.isLoading, 'cl-po--editing': controller.editingMode })}"
                            @click=${(e) => controller.onGenerateImage()}
                            ?disabled=${controller.isLoading}
                        >
                            ${controller.isLoading 
                                ? ('') : (controller.editingMode 
                                    ? Translate.get('product.options.ai.button.edit', null, 'Edit Image')
                                    : Translate.get('product.options.ai.button.generate', null, 'Generate'))}
                        </button>
                    </div>
                    
                    ${controller.errors.general ? html`
                        <div class="cl-po--ai-error">
                            ${controller.errors.general}
                        </div>
                    ` : null}
                </div>
                ` : null}
            ` : null}
                
                ${((controller.aiEdit || controller.aiRemoveBg) && controller.selectedImage) || controller.editingMode ? html`
                    <div class="cl-po--ai-actions ${classMap({ 'cl-po--ai-editing': controller.editingMode })}">
                        ${controller.editingMode ? html`
                                <span class="cl-po--ai-edit-icon">✏️</span>
                                ${Translate.get('product.options.ai.edit.indicator', null, 'Editing image')}
                                <button class="cl-po--ai-cancel-edit" @click=${(e) => controller.onCancelEdit()}>X</button>
                        ` : html`
                            ${controller.aiEdit ? html`
                                <button 
                                    class="cl-po--ai-action-button cl-po--ai-edit-button"
                                    @click=${(e) => controller.onEditImage(controller.selectedImage)}
                                    ?disabled=${controller.isLoading}
                                >
                                    <span class="cl-po--ai-edit-icon">✏️</span>
                                    ${Translate.get('product.options.ai.edit.button', null, 'Edit Image')}
                                </button>
                            ` : null}
                            
                            ${controller.aiRemoveBg ? html`
                                <button 
                                    class="cl-po--ai-action-button cl-po--ai-removebg-button ${classMap({ 'cl-po--ai-bg-removed': controller.selectedImage?.backgroundRemoved })}"
                                    @click=${(e) => controller.removeBackground(controller.selectedImage)}
                                    ?disabled=${controller.isLoading || controller.selectedImage?.backgroundRemoved}
                                >
                                    <span class="cl-po--ai-magic-wand">🪄</span>
                                    ${controller.selectedImage?.backgroundRemoved 
                                        ? Translate.get('product.options.ai.removebg.removed', null, 'Background Removed')
                                        : Translate.get('product.options.ai.removebg.button', null, 'Remove Background')}
                                </button>
                            ` : null}
                        `}
                    </div>
                ` : null}

            ${controller.uploadImage?.dirty ? html`
                <button
                        class="cl-po--ai-action-button cl-po--ai-removebg-button"
                        @click=${(e) => {
                            // re-apply based on the origianal upload
                            controller.uploadImage = {... controller.originalUploadImage };
                            controller.editUpload();
                        }}
                        ?disabled=${controller.isLoading}
                >
                    <span class="cl-po--ai-magic-wand">🪄</span>
                    ${Translate.get('product.options.ai.update.button', null, 'Update image')}
                </button>
            ` : null}
            
            ${controller.isLoading ? html`
                <div class="cl-po--ai-loading ${classMap({ 'cl-po--ai-loading-overlay': controller.imageHistory.length > 0 })}">
                    <div class="cl-po--ai-spinner"></div>
                    <div class="cl-po--ai-loading-text">
                        ${controller.editingMode 
                            ? Translate.get('product.options.ai.loading.editing', null, 'Editing image...')
                            : Translate.get('product.options.ai.loading.generating', null, 'Generating your image...')
                        }
                    </div>
                </div>
            ` : null}
            
            ${controller.imageHistory.length > 0 ? html`
                <div class="cl-po--ai-images">
                    <div class="cl-po--ai-flex-grid">
                        ${repeat(controller.imageHistory.slice(0, controller.displayedImagesCount), (image) => image.id, (image) => html`
                            <div class="cl-po--ai-flex-item">
                                <label class="cl-po--ai-image-item ${classMap({ 'cl-po--ai-image-selected': controller.selectedImage === image })}">
                                    <input
                                        type="radio"
                                        name="${optionName(option)}"
                                        value="${image.img}"
                                        .checked=${controller.selectedImage === image}
                                        @change=${(e) => controller.onImageSelect(e, image)}
                                        ?required=${option.required}
                                        form=${ifDefined(config.productFormLink)}
                                        class="cl-po--ai-image-input"
                                    >
                                    <div class="cl-po--ai-image-preview">
                                        <img src="${image.img}" />
                                    </div>
                                    <button type="button" class="cl-po--ai-preview-button" @click=${(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            controller.openOverlay(image);
                                        }}>🔍</button>
                                </label>
                            </div>
                        `)}
                    </div>
                    
                    ${controller.imageHistory.length > controller.displayedImagesCount ? html`
                        <div class="cl-po--ai-load-more">
                            <button 
                                class="cl-po--ai-load-more-button"
                                @click=${(e) => controller.onLoadMore()}
                            >
                                ${Translate.get('product.options.ai.load_more', null, 'Load More')} 
                                (${controller.imageHistory.length - controller.displayedImagesCount} remaining)
                            </button>
                        </div>
                    ` : null}
                </div>
            ` : null}
        </div>
    `;
};